'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { buildEmbedUrl, fetchVideoThumbnailUrl } from '@/lib/video/video.utils'
import { Loader } from '@/ui/loaders/loader/Loader'
import type React from 'react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'

export type VideoItem = {
  src: string
  poster?: string
  title?: string
  description?: string
}

/** Represents a video as received from the backend — only src is available */
export type ExternalVideoItem = StrictPick<VideoItem, 'src'>

export type VideoGalleryProps = {
  videos: VideoItem[]
  speed?: number
  onOpen?: () => void
  onClose?: () => void
}

export type VideoGalleryRef = {
  openGallery: (index?: number) => void
}

type IframeSlide = {
  type: 'iframe-video'
  src: string
  embedUrl: string
  poster?: string
  title?: string
  description?: string
}

function IframeSlideRenderer({
  slide,
  iframeKey,
  altText,
}: {
  slide: IframeSlide
  iframeKey: string
  altText: string
}) {
  const [loaded, setLoaded] = useState(false)

  return (
    <div className="relative flex size-full items-center justify-center">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader />
        </div>
      )}
      <iframe
        key={iframeKey}
        src={slide.embedUrl}
        title={slide.title || altText}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        onLoad={() => setLoaded(true)}
        className="aspect-video max-h-full w-full border-0"
      />
    </div>
  )
}

function VideoGallery(
  { videos, speed = 500, onOpen, onClose }: VideoGalleryProps,
  ref: React.Ref<VideoGalleryRef>,
): React.ReactElement {
  const { t } = useTranslation(['videoGallery'])
  const [open, setOpen] = useState(false)
  const openIndexRef = useRef(0)
  const [processedVideos, setProcessedVideos] = useState<VideoItem[]>(videos)

  // Fetch missing posters client-side when not provided via props
  useEffect(() => {
    const allPostersProvided = videos.every((video) => !!video.poster)

    if (allPostersProvided) {
      setProcessedVideos(videos)

      return
    }

    // Cancellation flag: prevents calling setProcessedVideos after the component unmounts
    // or after `videos` changes (triggering a new effect run), which would cause a stale
    // state update from a previous async fetch that resolved late.
    let cancelled = false

    Promise.all(
      videos.map(async (video) => {
        if (video.poster) {
          return video
        }

        const thumbnailUrl = await fetchVideoThumbnailUrl(video.src, 'maxres')

        return { ...video, poster: thumbnailUrl }
      }),
    ).then((updatedVideos) => {
      if (!cancelled) {
        setProcessedVideos(updatedVideos)
      }
    })

    return () => {
      cancelled = true
    }
  }, [videos])

  const openGallery = (index = 0): void => {
    openIndexRef.current = index
    setOpen(true)
    onOpen?.()
  }

  useImperativeHandle(ref, () => ({ openGallery }))

  const slides: IframeSlide[] = processedVideos.flatMap((video) => {
    const embedUrl = buildEmbedUrl(video.src)

    if (!embedUrl) return []

    const slide: IframeSlide = {
      type: 'iframe-video',
      src: video.src,
      embedUrl,
      poster: video.poster,
      title: video.title,
      description: video.description,
    }

    return [slide]
  })

  return (
    <Lightbox
      open={open}
      close={() => {
        setOpen(false)
        onClose?.()
      }}
      index={openIndexRef.current}
      slides={slides}
      carousel={{ padding: 64 }}
      animation={{ fade: speed }}
      render={{
        buttonPrev: slides.length <= 1 ? () => null : undefined,
        buttonNext: slides.length <= 1 ? () => null : undefined,
        slide: ({ slide, offset }) => {
          const iframeSlide = slide as IframeSlide

          if (iframeSlide.type !== 'iframe-video') return undefined

          const isActive = open && offset === 0

          return (
            <IframeSlideRenderer
              key={isActive ? iframeSlide.src : ''}
              slide={iframeSlide}
              iframeKey={isActive ? iframeSlide.src : ''}
              altText={t('videoGallery:image.alt')}
            />
          )
        },
      }}
    />
  )
}

export default forwardRef<VideoGalleryRef, VideoGalleryProps>(VideoGallery)
